import React, { useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Page, BlockStack } from "@shopify/polaris";
import { APP_DISPLAY_NAME, PARTNER_APP_ID } from "../config/appBranding";
import { useAppTitleBar } from "../hooks/useAppTitleBar";
import { useAppNavigate } from "../hooks/useAppNavigate";
import { useEnsureInstall } from "../hooks/useEnsureInstall";
import { useThemeEmbedStatus } from "../hooks/useThemeEmbedStatus";
import { useCalendarOnboarding } from "../hooks/useCalendarOnboarding";
import { useSyncCalendarMetafieldsAfterInstall } from "../hooks/useSyncCalendarMetafieldsAfterInstall";
import SetupGuideSection from "../components/dashboard/SetupGuideSection";
import ThemeAppEmbedStatusBanner from "../components/dashboard/ThemeAppEmbedStatusBanner";
import FaqSection from "../components/dashboard/FaqSection";
import RecommendedAppsSection from "../components/dashboard/RecommendedAppsSection";
import { getStorefrontUrl } from "../utils/shopDomain";

export default function Dashboard() {
  const navigate = useAppNavigate();
  const location = useLocation();
  const { shop, installChecked, installData } = useEnsureInstall();
  const shopDomain = shop || "";

  const preservedSearch = useMemo(
    () => location.search || window.location.search || "",
    [location.search],
  );

  const onGoCreateWidget = useCallback(() => {
    navigate({ pathname: "/widgets/new", search: preservedSearch });
  }, [navigate, preservedSearch]);

  useSyncCalendarMetafieldsAfterInstall(installChecked, installData);

  const {
    themeLoading,
    themeRefreshing,
    embedInactive,
    themeCheckError,
    refresh,
    getThemeEditorDeepLink,
  } = useThemeEmbedStatus(shopDomain);

  const onboarding = useCalendarOnboarding({
    installChecked,
    installData,
    themeLoading,
    embedInactive,
  });

  const storefrontUrl = useMemo(() => getStorefrontUrl(shopDomain), [shopDomain]);

  useAppTitleBar({ title: APP_DISPLAY_NAME });

  const setupProps = useMemo(
    () => ({
      themeLoading,
      themeRefreshing,
      embedInactive,
      themeCheckError,
      onRefreshThemes: refresh,
      getThemeEditorDeepLink,
      storefrontUrl,
      onDismissSetupGuide: onboarding.dismissSetupGuide,
      widgetsChecked: onboarding.widgetsChecked,
      step1Done: onboarding.step1Done,
      step2Done: onboarding.step2Done,
      step3Done: onboarding.step3Done,
      completedCount: onboarding.completedCount,
      onGoCreateWidget,
      onMarkTemplate: onboarding.markWidgetStep,
      onMarkSetupComplete: onboarding.markSetupComplete,
    }),
    [
      themeLoading,
      themeRefreshing,
      embedInactive,
      themeCheckError,
      refresh,
      getThemeEditorDeepLink,
      storefrontUrl,
      onboarding.dismissSetupGuide,
      onboarding.widgetsChecked,
      onboarding.step1Done,
      onboarding.step2Done,
      onboarding.step3Done,
      onboarding.completedCount,
      onGoCreateWidget,
      onboarding.markWidgetStep,
      onboarding.markSetupComplete,
    ],
  );

  return (
    <Page>
      <BlockStack gap="500">
        {!onboarding.setupGuideDismissed ? (
          <SetupGuideSection {...setupProps} />
        ) : (
          <ThemeAppEmbedStatusBanner
            themeLoading={themeLoading}
            themeRefreshing={themeRefreshing}
            embedInactive={embedInactive}
            themeCheckError={themeCheckError}
            onRefreshThemes={refresh}
            getThemeEditorDeepLink={getThemeEditorDeepLink}
          />
        )}
        <FaqSection />
        <RecommendedAppsSection currentAppId={PARTNER_APP_ID} />
      </BlockStack>
    </Page>
  );
}
